import type { QuoteType } from 'state/quotes/infos/infos.model';

export type SmartQuoteAction = {
  disclaimer:
    | 'open - header info button'
    | 'open - quick start learn more'
    | 'open - quick start available product types'
    | 'close - x button'
    | 'close - dismiss button'
    | 'close - escape button';
  'reset tab': 'open confirm' | 'confirm' | 'close - x button' | 'close - cancel button';
  'quick start': 'expand' | 'collapse - header' | 'collapse - dismiss';
  email: 'input bar - add button' | 'drag and drop';
  'select client': 'from input bar' | 'from prompt';
  'request quote': 'send button' | 'prompt client';
  'quote actions':
    | 'copy quotes'
    | 'share feedback'
    | 'open in advanced workspace'
    | 'open in batch workspace'
    | 'open in quote blotter';
  'table actions': 'open in advanced workspace';
};

export type AnalyticCategoryActions = {
  'Primary market smart quote': keyof SmartQuoteAction;
  'Addon Management Workspace':
    | 'Update filter'
    | 'Request for trade'
    | 'Refresh workspace'
    | 'Request for tradable quote'
    | 'Request for executable quote'
    | 'Trade done';
  Addon:
    | 'Add custodian'
    | 'Open greeks modal'
    | 'Remove custodian'
    | 'Cancel tradable quote'
    | 'Request for trade'
    | 'Request for tradable quote'
    | 'Request for exec only tradable quote'
    | 'Request for advanced tradable quote'
    | 'Merges deal'
    | 'Cancel quote'
    | 'Request for quote'
    | PriceImprovement;
  Backtest:
    | 'Open backtest'
    | 'Run backtest'
    | 'Cancel backtest'
    | 'Date range backtest'
    | 'Custom dates backtest'
    | 'Download results';
  'Batch Workspace':
    | 'Select a product'
    | 'Select an item'
    | 'Deselect an item'
    | 'Select all items'
    | 'Deselect all items'
    | 'Export internal csv from menu'
    | 'Export internal csv from header'
    | 'Export internal excel from header'
    | 'Export external csv from menu'
    | 'Export external csv from header'
    | 'Export external excel from header'
    | 'Delete from menu'
    | 'Open in quote workspace from menu'
    | 'Delete from header'
    | 'Duplicate from menu'
    | 'Duplicate from header'
    | 'Price from menu'
    | 'Price from header'
    | 'Reset tab from header'
    | 'Refresh dates'
    | 'Refresh dates from header'
    | 'Add an item'
    | 'Cancel quote'
    | 'Cancel quote(s) from header'
    | 'Open batch apply modal'
    | 'Open quote(s) in Quote Workspace from header'
    | 'Apply batch apply'
    | 'Cancel batch apply'
    | 'Navigate horizontally (Tab)'
    | 'Edit field (Space)'
    | 'Navigate with arrow key (Arrow key)'
    | 'Confirm field value (Enter)'
    | 'Clear field (Delete)'
    | 'Copy field (Ctrl+C)'
    | 'Copy field (Ctrl+X)'
    | 'Paste field (Ctrl+V)'
    | 'Edit numeric field (Numeric key)'
    | 'Edit input field (Numeric key)'
    | 'Price selected form (Ctrl+Enter)'
    | 'Delete selected form (Ctrl+Delete)'
    | '(Un)select form (Ctrl+Space)'
    | 'Duplicate form (Ctrl+M)'
    | 'Reset current tab (Ctrl+Backspace)';
  'Blotter My Products': 'Download marketing materials' | CommonBlotterAction;
  'Blotter My flow products': 'Open in secondary' | 'Download final term' | CommonBlotterAction;
  'Blotter My flow products trades': '';
  'Blotter My Quotes': 'Select Missed Deal Feedback tab' | CommonBlotterAction;
  'Blotter Secondary Market trades': 'Open product' | CommonBlotterAction;
  'Blotter Secondary Market orders': CommonBlotterAction;
  'Blotter My Trades': CommonBlotterAction;
  'Blotter My price improvements':
    | 'Validate'
    | 'Reject'
    | 'Acknowledge'
    | 'Comment'
    | 'Adjust price'
    | 'Dismiss error'
    | CommonBlotterAction;
  'Blotter Scheduled quotes': CommonBlotterAction;
  'Central Station':
    | 'Deselect all items'
    | 'Select all items'
    | 'Invert selection'
    | 'Book selected items'
    | 'Remove all items'
    | 'Add an item to Order Basket';
  Discover:
    | 'Apply Product Advisor settings'
    | 'Open Product Advisor quote'
    | 'Expand Lifecycle category'
    | 'Export rankings CSV'
    | 'Export trendings CSV'
    | 'Open Lifecycle portfolio'
    | 'Open Lifecycle product detail'
    | 'Open New Static Screen'
    | 'Open News portal'
    | 'Open Product publication'
    | 'Open Research portal'
    | 'Open Thematic publication'
    | 'Open Top trades portal'
    | 'Open Video publication'
    | 'Reset Product Advisor settings'
    | 'Close Product Advisor settings'
    | 'Open Product Advisor settings'
    | 'See more Product publication'
    | 'See more Thematic publication'
    | 'See more Video publication'
    | 'Select Lifecycle tab'
    | 'Select Product Advisor tab'
    | 'Select region'
    | 'Select Statistics Monthly period tab'
    | 'Select Statistics Rankings tab'
    | 'Select Statistics Trendings tab'
    | 'Select Statistics Weekly period tab'
    | 'Slide Product publication'
    | 'Slide Thematic publication'
    | 'Slide Video publication'
    | 'See more videos - Select category'
    | 'See more videos - Select keyword'
    | 'See more videos - Play from thumbnail in video list'
    | 'See more videos - Play from preview'
    | 'See more videos - Slide video list left'
    | 'See more videos - Slide video list right';
  'Email service': 'Open email service modal' | 'Change recipient' | 'Preview email' | 'Send email';
  'Missed Deal Feedback': 'Confirm the submission' | 'Decline the submission' | 'Discard feedback' | 'Submit feedback';
  'Help Center':
    | 'Close Help Center'
    | 'Open Help Center'
    | 'Never show keyboard shortcut banner again'
    | 'Dismiss keyboard shortcut banner'
    | 'Open keyboard shortcut'
    | `Click on "${string}"`;
  'Orders Basket': 'Add an item to Order Basket';
  'Primary market': CommonWorkspaceAction;
  'Primary market dynamic': CommonWorkspaceAction;
  'Primary market static': CommonWorkspaceAction;
  'Primary market essential': EssentialWorkspaceProductSelectorAction;
  'Primary market essential product selector': EssentialProductSelectorAction;
  'Primary market product selector static': CommonWorkspaceProductSelectorAction;
  'Primary market product selector newBatch': CommonWorkspaceProductSelectorAction;
  'Product modification': 'Reload product modification' | 'Download termsheet' | 'Termsheet';
  Regulation: 'Download PG_Language' | 'Download KID_Language';
  'Secondary market':
    | 'Amend CP order form'
    | 'Amend limit order form'
    | 'Amend live trade form'
    | 'Apply amend'
    | 'Clear all filters'
    | 'Clear search result'
    | 'Confirm trade'
    | 'Cancel quote'
    | 'Delete product'
    | 'Delete all products'
    | 'Instant book workflow aborted'
    | 'Instant book workflow initiated'
    | 'Instant book workflow completed'
    | 'Instant book workflow requested'
    | 'Limit order cancellation workflow aborted'
    | 'Limit order cancellation workflow completed'
    | 'Limit order cancellation workflow initiated'
    | 'Limit order cancellation workflow requested'
    | 'Live order cancellation workflow aborted'
    | 'Live order cancellation workflow completed'
    | 'Live order cancellation workflow initiated'
    | 'Live order cancellation workflow requested'
    | 'Open CP Order form'
    | 'Open greeks'
    | 'Open Lifecycle portfolio'
    | 'Open Limit order form'
    | 'Open Limit Order screen'
    | 'Open Live trade'
    | 'Open order feedback modal'
    | 'Open product details modal'
    | 'Open Real Time screen'
    | 'Open RT quote form'
    | 'Order cancellation workflow aborted'
    | 'Order cancellation workflow completed'
    | 'Order cancellation workflow initiated'
    | 'Order cancellation workflow requested'
    | 'Product Loading'
    | 'Product search by ISIN'
    | 'Product search by FILE_CODE'
    | 'Product search by ELIOT'
    | 'Product search by ELIOT OTC'
    | 'Product search by XONE'
    | 'Product search by WKN'
    | 'Product search by CUSIP'
    | 'Product search by VALOREN'
    | 'Refresh all products'
    | 'Refresh product'
    | 'Reprice quote'
    | 'Request for limit order trade'
    | 'Request for live quote'
    | 'Request for place order'
    | 'Request for dlp place order'
    | 'Request for quote'
    | 'Request for trade'
    | 'Reset to quote'
    | 'Show valuation details'
    | 'Start exporting products'
    | 'View in trade blotter'
    | 'Send mail to me'
    | 'Share product summary';
  'Secondary market batch':
    | 'Open in secondary market'
    | 'Open secondary trade blotter'
    | 'Open secondary post trade'
    | 'Remove'
    | 'Reset'
    | 'Place orders'
    | 'Get quotes'
    | 'Confirm to trade'
    | 'Download'
    | 'Cancel quote'
    | 'Switch to CP orders tab'
    | 'Switch to Quotes tab'
    | 'Switch to Live quotes tab'
    | 'Product search by ISIN'
    | 'Product search by FILE_CODE'
    | 'Product search by ELIOT'
    | 'Product search by ELIOT OTC'
    | 'Product search by XONE'
    | 'Product search by WKN'
    | 'Product search by CUSIP'
    | 'Product search by VALOREN';
  'SG Advisory':
    | 'Cancel advise me'
    | 'Start advise me'
    | 'Start optimization'
    | 'Cancel optimization'
    | 'Quote from optimized priced'
    | 'Quote from recommendation priced';
  'Trade allocation':
    | 'Add allocation'
    | 'Reset trade allocation'
    | 'Apply'
    | 'Reset'
    | 'Ok'
    | 'Open in Add-on'
    | 'Open in secondary market post trade';
  'User preferences': UserPreferenceAnalyticAction;
  'Underlying universe':
    | 'Select Equities tab'
    | 'Select Commodities tab'
    | 'Select Credit tab'
    | 'Select Forex tab'
    | 'Select Rate tab'
    | 'Export CSV';
  Workspace:
    | 'Addon Management screen'
    | 'Discover'
    | 'Smart quote'
    | 'Essential workspace'
    | 'New static screen'
    | 'New dynamic screen'
    | 'Batch pricer'
    | 'New batch screen'
    | 'Dynamic (Rfq builder)'
    | 'SG Markets bespoke'
    | 'Underlying universe'
    | 'My quotes blotter'
    | 'My trades blotter'
    | 'Price improvement blotter'
    | 'Orders basket'
    | 'Secondary market'
    | 'Secondary market products search'
    | 'Secondary market quotes workspace'
    | 'Secondary market batch workspace'
    | 'Secondary market post event workspace'
    | 'Secondary market trades blotter'
    | 'Secondary market orders blotter'
    | 'Help request send request'
    | 'Help request open modal'
    | 'Scheduled quotes blotter';
  'Scheduled run':
    | 'Create from batch'
    | 'Create from static'
    | 'Create from dynamic'
    | 'Create from quotes blotter'
    | 'Create from trades blotter'
    | 'Amend from batch'
    | 'Amend from static'
    | 'Amend from dynamic'
    | 'Amend from quotes blotter'
    | 'Amend from trades blotter'
    | 'Suspend'
    | 'Reschedule'
    | 'Delete'
    | 'Adjust schedule'
    | 'Manage quotes';
  'Active quotes dashboard': DashboardAnalyticAction;
};

export type WorkspaceAnalyticCategory = keyof Pick<
  AnalyticCategoryActions,
  | 'Primary market'
  | 'Primary market dynamic'
  | 'Primary market static'
  | 'Primary market essential'
  | 'Primary market smart quote'
>;

export type BlotterAnalyticCategory = keyof Pick<
  AnalyticCategoryActions,
  | 'Blotter My Products'
  | 'Blotter My Quotes'
  | 'Blotter My Trades'
  | 'Blotter My price improvements'
  | 'Blotter My flow products'
  | 'Blotter My flow products trades'
>;

export type CommonWorkspaceProductSelectorAction =
  | 'Change tab'
  | 'Click on button'
  | 'Search for product'
  | 'Pin product - Most recent'
  | 'Pin product - Catalogue'
  | 'Unpin product - Pinned'
  | 'Unpin product - Most recent'
  | 'Unpin product - Catalogue'
  | 'Quick start - Pinned'
  | 'Quick start - Most recent'
  | 'Quick start - Catalogue'
  | 'Open tutorial'
  | 'Search for client'
  | 'Select client'
  | 'Select product';

export type EssentialProductSelectorAction =
  | ProductHelpCenterAction
  | 'Open asset shortcut'
  | 'Pin product'
  | 'Unpin product'
  | 'Select a product';

export type EssentialWorkspaceProductSelectorAction =
  | 'Open in advanced workspace'
  | 'Close tab'
  | 'Clear all tabs confirmed'
  | 'Reset tab'
  | 'Refresh dates'
  | 'Reset schedule'
  | 'Clone tab'
  | 'Change tab'
  | 'Rename tab'
  | 'Cancel quote (From status)'
  | 'Request for quote'
  | 'New quote'
  | 'Request for documentation'
  | 'Import quotes - Max number of quotes to import reached'
  | 'Import quotes - successfully imported'
  | 'Import quotes - discarded due to incorrect identifier format'
  | 'Contact help center';

type CommonWorkspaceAction =
  | 'Clear all underlyings'
  | 'Clear all tabs'
  | 'Expend panel'
  | 'Reprice dailyBlotter'
  | 'Clone tab'
  | 'Open in batch workspace'
  | 'Close tab'
  | 'Clear all tabs confirmed'
  | 'Add tab'
  | 'Add custodian'
  | 'Copy metrics'
  | 'Copy vol sto'
  | 'Copy greek'
  | 'Copy Fees'
  | 'Copy sensitivities'
  | 'Copy historical average correlations'
  | 'Copy prices'
  | 'Copy product greeks'
  | 'Underlying website'
  | 'Switch underlyings'
  | 'Remove underlyings'
  | 'Reset schedule'
  | 'Refresh dates'
  | 'Compute vol sto'
  | 'Cancel vol sto'
  | 'Accepted french documentation'
  | 'Enable Back to back'
  | 'Accepted kid documentation'
  | 'Accepted product termsheet'
  | 'Cancel quote (From status)'
  | 'MIFID2 cost & charges'
  | 'Theoretical value'
  | 'KID'
  | 'MIFID2 target market results'
  | 'Target market Map'
  | 'Raw value for money'
  | 'Quote details'
  | 'Greeks or Fees or Metrics'
  | 'Greeks'
  | 'Fees'
  | 'Metrics'
  | 'Product dump'
  | 'Download tutorial'
  | 'Download termsheet'
  | 'Download final terms'
  | 'Download marketing material'
  | 'Download information fact sheet'
  | 'Download supplement distribution agreement'
  | 'Open tutorial'
  | 'Download factsheet'
  | 'Download brochure'
  | 'Start video'
  | 'Display summary'
  | 'Download KID language'
  | 'Download target market language'
  | 'Termsheet'
  | 'Reset tab'
  | 'Reset tab from shortcut'
  | 'Paste in Grid'
  | 'Download schedule'
  | 'Remove custodian'
  | 'Trade done'
  | 'Request for trade'
  | 'Cancel tradable quote'
  | 'Request for trade confirmation'
  | 'Request for quote'
  | 'Request for advanced indicative quote'
  | 'Request for tradable quote'
  | 'Request for advanced tradable quote'
  | 'Request for exec only quote'
  | 'Request for advanced exec only quote'
  | 'Request for quote with regulation'
  | 'Request for executable quote'
  | 'Request for advanced executable quote'
  | 'Request for book with execution'
  | 'Request for book without execution'
  | 'Reprice quote'
  | 'Reprice advanced indicative quote'
  | 'Reprice tradable quote'
  | 'Reprice advanced tradable quote'
  | 'Reprice exec only quote'
  | 'Reprice advanced exec only quote'
  | 'Reprice quote with regulation'
  | 'Reprice executable quote'
  | 'Reprice advanced executable quote'
  | 'Reprice book with execution'
  | 'Reprice book without execution'
  | 'Get pre-trade codes'
  | SelectSidePanelTabAction
  | PriceImprovement
  | 'Display Underlying Historics'
  | ProductHelpCenterAction;

export type ProductHelpCenterAction = 'Open tutorial' | 'Download factsheet' | 'Start video';

export type SelectSidePanelTabAction =
  | 'Select Advisory tab'
  | 'Select Backtest tab'
  | 'Select Missed Deal Feedback tab'
  | 'Select Quotation tab'
  | 'Select Quote tab'
  | 'Select Regulation tab'
  | 'Select VolSto tab';

type CommonBlotterAction =
  | 'Reload (New Static Screen)'
  | 'Reload (New Dynamic Screen)'
  | 'Reprice batch'
  | 'Addon'
  | 'Trade cancellation workflow aborted'
  | 'Trade cancellation'
  | 'Trade cancellation workflow initiated'
  | 'Modify product (Rfq)'
  | 'Download preconfirmation'
  | 'Download brochure'
  | 'Download termsheet'
  | 'Trade allocation'
  | 'New Trade allocation'
  | 'Download KID language'
  | 'Download target market language'
  | 'Download CSV'
  | 'Export CSV'
  | 'Export Excel'
  | 'Clear all filters';

export type DashboardAnalyticAction =
  | 'Open dashboard'
  | 'Close dashboard'
  | 'Open tab through dashboard'
  | 'Reprice tile though dashboard'
  | 'Close a dashboard tile'
  | 'Closing many dashboard tiles'
  | 'Dashboard switch to filtering by status'
  | 'Dashboard switch to filtering by client/product'
  | 'Dashboard switch to filtering by group/pin'
  | 'Pin or unpin a dashboard tile'
  | 'Group a tile in a custom group'
  | 'Rename a custom group'
  | 'Open a termsheet';

export type UserPreferenceAnalyticAction =
  | 'Change tab order/visibility'
  | 'Set batch screen currency'
  | 'Set batch screen product type'
  | 'Set batch screen product subtype'
  | 'Set batch screen wrapper'
  | 'Set default client'
  | 'Set default xMargin'
  | 'Set default landing page'
  | 'Set fees precision'
  | 'Set greeks precision'
  | 'Set backtest precision'
  | 'Set metrics precision'
  | 'Set private mode'
  | 'Set product type'
  | 'Set product subtype'
  | 'Set theme'
  | 'Set underlying code type'
  | 'Set secondary market product search default code type'
  | 'Set volSto precision'
  | 'Set wrapper'
  | 'Set currency'
  | 'Switch to advanced quote workspace'
  | 'Switch to standard quote workspace'
  | 'Switch to advanced batch workspace'
  | 'Switch to standard batch workspace'
  | 'Set default discover page region';

export type PriceImprovement =
  | 'Request price improvement'
  | 'Cancel price improvement'
  | 'Apply price improvement'
  | 'Discard price improvement';

export const quoteTypeToTracking: Record<
  QuoteType,
  AnalyticCategoryActions['Primary market static' | 'Primary market dynamic']
> = {
  Indicative: 'Reprice quote',
  AdvancedIndicative: 'Reprice advanced indicative quote',
  Tradable: 'Reprice tradable quote',
  AdvancedTradable: 'Reprice advanced tradable quote',
  ExecOnly: 'Reprice exec only quote',
  AdvancedExecOnly: 'Reprice advanced exec only quote',
  Executable: 'Reprice executable quote',
  AdvancedExecutable: 'Reprice advanced executable quote',
  BookWithExecution: 'Reprice book with execution',
  BookWithoutExecution: 'Reprice book without execution',
};
